/**
 * Settings form component.
 * Dynamic form for connector settings based on JSON schema.
 */

// React Imports
import { useState, useEffect } from 'react';
import { Chip, FormField, Textarea } from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';
import type { ConnectorSettingsDto } from '../../types/connector';

export interface SettingsFormProps {
  settings?: ConnectorSettingsDto[];
  settingsJson?: string;
  level?: 'System' | 'Tenant' | 'Project' | 'Instance';
  onChange?: (settingsJson: string) => void;
  readOnly?: boolean;
}

/**
 * Settings form component.
 * Renders a dynamic form based on settings JSON schema.
 */
export const SettingsForm = ({
  settings,
  settingsJson,
  level = 'Instance',
  onChange,
  readOnly = false,
}: SettingsFormProps) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (settingsJson) {
      try {
        const parsed = JSON.parse(settingsJson);
        setFormData(parsed);
      } catch {
        setFormData({});
      }
    } else if (settings && settings.length > 0) {
      const merged: Record<string, any> = {};
      settings.forEach((s) => {
        try {
          const parsed = JSON.parse(s.settingsJson);
          Object.assign(merged, parsed);
        } catch {
          // Ignore invalid JSON
        }
      });
      setFormData(merged);
    }
  }, [settingsJson, settings]);

  return (
    <FormSection
      title="Settings Configuration"
      description={level ? `Configuration level: ${level}` : undefined}
      data-testid="connector-settings-section"
    >
      <div className="flex items-center justify-end mb-2">
        {level && (
          <Chip
            label={`Level: ${level}`}
            color="accent"
            variant="outlined"
          />
        )}
      </div>

      {settings && settings.length > 0 && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800/40 dark:bg-blue-950/50 dark:text-blue-300 mb-4">
          Settings are inherited from: {settings.map((s) => s.level).join(' → ')}
        </div>
      )}

      <FormField
        label="Settings JSON"
        description="Enter settings as JSON. This will be merged with inherited settings."
        error={errors.json}
        data-testid="connector-settings-json"
      >
        <Textarea
          className={`w-full font-mono ${errors.json ? 'border-red-500' : ''}`}
          value={JSON.stringify(formData, null, 2)}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            try {
              const parsed = JSON.parse(e.target.value);
              setFormData(parsed);
              setErrors({});
              if (onChange) {
                onChange(e.target.value);
              }
            } catch (error) {
              setErrors({ json: 'Invalid JSON format' });
            }
          }}
          rows={10}
          disabled={readOnly}
          data-testid="connector-settings-json"
        />
      </FormField>

      {Object.keys(formData).length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium mb-2">
            Current Settings:
          </p>
          <div className="p-4 bg-muted/40 rounded font-mono text-xs">
            <pre className="m-0">{JSON.stringify(formData, null, 2)}</pre>
          </div>
        </div>
      )}
    </FormSection>
  );
};
